<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="reviews-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Recenzje</h1>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="reviews"
          class="elevation-1"
          :loading="loading"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-star</v-icon></div>
              <div class="filter-title">{{ $t('pointsReviews.title') }}</div>
            </div>
          </template>
          <template v-slot:item.linkOrScreenshot="{ item }">
            <a
              v-if="item.link"
              :href="item.link"
              target="_blank"
              style="text-decoration: underline; cursor: pointer; color: #1976d2;"
            >
              {{ $t('pointsReviews.reviewLink') }}
            </a>
            <v-btn
              v-if="item.screenshotUrl"
              small
              color="secondary"
              class="ml-2"
              @click="openScreenshot(item.screenshotUrl)"
            >
              <v-icon small left>mdi-image</v-icon>
              Podgląd
            </v-btn>
            <span v-if="!item.link && !item.screenshotUrl" class="grey--text">-</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small color="success" class="mr-2" @click="approveReview(item)">
              <v-icon small>mdi-check</v-icon>
            </v-btn>
            <v-btn small color="error" @click="openRejectDialog(item)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Screenshot Preview Dialog -->
    <v-dialog v-model="screenshotDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('pointsReviews.screenshotPreview') }}</span>
          <v-btn icon @click="screenshotDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="text-center pa-2">
          <v-img :src="screenshotPreviewUrl" max-height="600" contain></v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <a :href="screenshotPreviewUrl" target="_blank" class="mr-4" style="text-decoration: none">
            <v-btn color="primary" text><v-icon left>mdi-open-in-new</v-icon>{{ $t('pointsReviews.openInNewTab') }}</v-btn>
          </a>
          <v-btn text @click="screenshotDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Reason Dialog -->
    <v-dialog v-model="rejectDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('pointsReviews.rejectReview') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="rejectForm" v-model="rejectFormValid">
            <v-textarea
              v-model="rejectReason"
              label="Powod odrzucenia"
              outlined
              :rules="[rules.required]"
              rows="3"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="secondary" @click="rejectDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" :disabled="!rejectFormValid" @click="rejectReview">{{ $t('common.reject') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
import * as dayjs from "dayjs";
export default {
  data() {
    return {
      loading: false,
      reviews: [],
      headers: [
        { text: "Kierowca", value: "driverName", sortable: false },
        { text: "Portal", value: "portal", sortable: false },
        { text: "Data", value: "date", sortable: false },
        { text: "Link / Screenshot", value: "linkOrScreenshot", sortable: false },
        { text: "Akcje", value: "actions", sortable: false },
      ],
      screenshotDialog: false,
      screenshotPreviewUrl: null,
      rejectDialog: false,
      rejectFormValid: false,
      rejectReason: "",
      rejectItem: null,
      rules: {
        required: (v) => !!v || "Wymagane",
      },
    };
  },
  async created() {
    await this.initialize();
  },
  methods: {
    getAuthHeaders() {
      return {
        Authorization: "Bearer " + localStorage.getItem("token"),
      };
    },
    async toggleLoading(type) {
      localStorage.setItem("loading", type);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async initialize() {
      this.loading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "points/admin/reviews/pending",
          headers: this.getAuthHeaders(),
        });
        const data = res.data.data || res.data;
        const result = Array.isArray(data) ? data : data.result || data.items || [];
        this.reviews = result.map((item) => ({
          id: item.id,
          driverName: item.driver
            ? item.driver.firstName + " " + item.driver.lastName
            : "-",
          portal: item.portal || "-",
          date: item.createdAt
            ? dayjs(item.createdAt).format("DD-MM-YYYY")
            : "-",
          link: item.link || null,
          screenshotUrl: item.screenshotUrl || null,
        }));
      } catch (e) {
        console.log(e);
        Swal.fire(
          "Blad",
          e.response?.data?.message?.toString() || "Wystapil blad",
          "error"
        );
      }
      this.loading = false;
    },
    async approveReview(item) {
      await this.toggleLoading(true);
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "points/admin/reviews/approve",
          headers: this.getAuthHeaders(),
          data: { reviewId: item.id },
        });
        Swal.fire("Zatwierdzono", "Opinia zostala zatwierdzona", "success");
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire(
          "Blad",
          e.response?.data?.message?.toString() || "Wystapil blad",
          "error"
        );
      }
      await this.toggleLoading(false);
    },
    openRejectDialog(item) {
      this.rejectItem = item;
      this.rejectReason = "";
      this.rejectDialog = true;
      this.$nextTick(() => {
        if (this.$refs.rejectForm) this.$refs.rejectForm.resetValidation();
      });
    },
    async rejectReview() {
      if (!this.$refs.rejectForm.validate()) return;
      this.rejectDialog = false;
      await this.toggleLoading(true);
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "points/admin/reviews/reject",
          headers: this.getAuthHeaders(),
          data: {
            reviewId: this.rejectItem.id,
            reason: this.rejectReason,
          },
        });
        Swal.fire("Odrzucono", "Opinia zostala odrzucona", "success");
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire(
          "Blad",
          e.response?.data?.message?.toString() || "Wystapil blad",
          "error"
        );
      }
      await this.toggleLoading(false);
    },
    openScreenshot(url) {
      this.screenshotPreviewUrl = url;
      this.screenshotDialog = true;
    },
  },
};
</script>

<style scoped>
.reviews-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.table-section-title { display: flex; align-items: center; gap: 10px; padding: 14px 16px 12px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.filter-icon { width: 32px; height: 32px; border-radius: 10px; background: rgba(41,98,255,0.18); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.filter-title { font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.01em; flex-grow: 1; }

.reviews-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.reviews-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.reviews-page >>> .v-data-table tbody tr { background: #232838 !important; }
.reviews-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.reviews-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.reviews-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.reviews-page >>> .v-data-table a { color: #5478FF !important; }
.reviews-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.reviews-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.reviews-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.reviews-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>
